import PropTypes from "prop-types";
import { ArrowLeftOutlined, SearchOutlined, ShoppingOutlined, TeamOutlined } from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Button, Spin } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import PhotoIcon from "../../../assets/icons/photo.svg";
import CustomPagination from "../../../components/custom-component/CustomPagination";
import CarListCards from "./CarListCards";
import { RowActionsMenu } from "../../../components/data-table";

const pad2 = (value) => String(value ?? 0).padStart(2, "0");

function CategoryDetailsContent({
    categoryData,
    initialLoading,
    isCarPricingMode,
    formatRateDisplay,
    cars,
    carsLoading,
    totalItems,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    onBack,
    categoryActionItems,
    onCategoryActionClick,
    categoryActionLoading,
    onCarActionClick,
    carSearchValue,
    onCarSearchChange,
}) {
    const { t } = useTranslation("cars");
    const { t: tTopBar } = useTranslation("topBar");

    const carActionItems = useMemo(
        () => [
            { label: t("carDetails"), key: "carDetails" },
            { label: t("rates.exchangeCategory"), key: "exchangeCategory" },
        ],
        [t],
    );

    const pricingTiers = useMemo(
        () =>
            !isCarPricingMode
                ? [
                      { key: "hourlyRate", label: t("rates.perHour") },
                      { key: "dailyRate", label: t("rates.perDay") },
                      { key: "weeklyRate", label: t("rates.perWeek") },
                      { key: "monthlyRate", label: t("rates.perMonth") },
                  ]
                : [],
        [isCarPricingMode, t],
    );

    const isCategoryActive = categoryData?.status === "active";

    if (initialLoading) {
        return (
            <div className="flex min-h-[240px] items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="relative flex min-w-0 flex-col gap-4">
            {categoryActionLoading ? (
                <div
                    className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/70"
                    aria-busy="true"
                    aria-live="polite">
                    <Spin size="large" />
                </div>
            ) : null}
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined className="text-base text-[#2F2F46]" />}
                        onClick={onBack}
                        aria-label={tTopBar("goBack")}
                        className="!h-9 !w-9 shrink-0 !rounded-lg !p-0 hover:!bg-[#F2F2FA]"
                    />
                    <h1 className="truncate text-lg font-bold capitalize text-[#2F2F46]">
                        {t("rates.categoryDetails")}
                    </h1>
                </div>
                {categoryActionItems?.length ? (
                    <RowActionsMenu
                        items={categoryActionItems}
                        onClick={onCategoryActionClick}
                        label={t("action")}
                        disabled={categoryActionLoading}
                        loading={categoryActionLoading}
                        icon={<BsThreeDotsVertical className="text-lg text-[#667085]" />}
                        className="!h-9 !w-9 shrink-0 !rounded-lg !p-0 hover:!bg-[#F2F2FA]"
                    />
                ) : null}
            </div>

            <div className="flex flex-col items-center rounded-2xl border border-[#E5E7EB]/80 bg-white px-4 py-6 shadow-sm ring-1 ring-black/[0.03]">
                <div className="mb-3 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F2F4F7] ring-1 ring-black/[0.06]">
                    {categoryData?.image ? (
                        <img src={categoryData.image} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <img src={PhotoIcon} alt="" className="h-10 w-10 opacity-40" />
                    )}
                </div>
                <h2 className="m-0 text-center text-xl font-bold text-[#2F2F46]">
                    {categoryData?.categoryName || t("notAvailable")}
                </h2>
                <div className="mt-2 flex items-center justify-center gap-1.5">
                    <span
                        className={`h-2 w-2 shrink-0 rounded-full ${isCategoryActive ? "bg-[#22C55E]" : "bg-[#EF4444]"}`}
                        aria-hidden
                    />
                    <span
                        className={`text-sm font-medium ${isCategoryActive ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                        {isCategoryActive ? t("rates.active") : t("rates.deactive")}
                    </span>
                </div>
            </div>

            {!isCarPricingMode ? (
                <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-[#E5E7EB]/80 bg-white p-4 shadow-sm ring-1 ring-black/[0.03]">
                        <TeamOutlined className="mb-2 text-lg text-[#98A2B3]" />
                        <p className="m-0 text-xs text-[#667085]">{t("seats")}</p>
                        <p className="m-0 mt-1 text-2xl font-bold tabular-nums text-[#2F2F46]">
                            {categoryData?.seats != null ? pad2(categoryData.seats) : t("notAvailable")}
                        </p>
                    </div>
                    <div className="rounded-2xl border border-[#E5E7EB]/80 bg-white p-4 shadow-sm ring-1 ring-black/[0.03]">
                        <ShoppingOutlined className="mb-2 text-lg text-[#98A2B3]" />
                        <p className="m-0 text-xs text-[#667085]">{t("rates.bags")}</p>
                        <p className="m-0 mt-1 text-2xl font-bold tabular-nums text-[#2F2F46]">
                            {categoryData?.bags != null ? pad2(categoryData.bags) : t("notAvailable")}
                        </p>
                    </div>
                </div>
            ) : null}

            {!isCarPricingMode && pricingTiers.length > 0 ? (
                <div className="flex flex-col gap-3">
                    <p className="m-0 text-xs font-semibold uppercase tracking-wide text-[#98A2B3]">
                        {t("rates.setPrice")}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        {pricingTiers.map(({ key, label }) => (
                            <div
                                key={key}
                                className="rounded-2xl border border-[#E5E7EB]/80 bg-white px-4 py-3 shadow-sm ring-1 ring-black/[0.03]">
                                <p className="m-0 text-xs text-[#667085]">{label}</p>
                                <p className="m-0 mt-1 text-lg font-bold tabular-nums text-[#2F2F46]">
                                    {formatRateDisplay(categoryData?.[key], t)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="flex flex-col gap-2">
                <h2 className="m-0 text-sm font-bold uppercase tracking-wide text-[#2F2F46]">
                    {t("carList")}
                    <span className="text-[#5D5DF6]"> ({totalItems})</span>
                </h2>
                <p className="m-0 text-sm text-[#98A2B3]">{t("rates.categoryCarsSubheading")}</p>
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 focus-within:border-[#5D5DF6]">
                <SearchOutlined className="text-gray-400" />
                <input
                    type="search"
                    value={carSearchValue}
                    onChange={(event) => onCarSearchChange(event.target.value)}
                    placeholder={t("mobileList.searchPlaceholder")}
                    className="w-full min-w-0 border-none bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
                />
            </div>

            <CarListCards
                cars={cars}
                loading={carsLoading}
                t={t}
                isCarPricingMode={isCarPricingMode}
                actionItems={carActionItems}
                onActionClick={onCarActionClick}
            />

            {totalItems > 0 ? (
                <div className="flex min-w-0 w-full justify-end">
                    <CustomPagination
                        variant="spaced"
                        totalItem={totalItems}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSizeOptions={[10, 20, 50, 100]}
                        showSizeChanger
                    />
                </div>
            ) : null}
        </div>
    );
}

CategoryDetailsContent.propTypes = {
    categoryData: PropTypes.object,
    initialLoading: PropTypes.bool.isRequired,
    isCarPricingMode: PropTypes.bool.isRequired,
    formatRateDisplay: PropTypes.func.isRequired,
    cars: PropTypes.arrayOf(PropTypes.object).isRequired,
    carsLoading: PropTypes.bool.isRequired,
    totalItems: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    setCurrentPage: PropTypes.func.isRequired,
    pageSize: PropTypes.number.isRequired,
    setPageSize: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    categoryActionItems: PropTypes.arrayOf(PropTypes.object),
    onCategoryActionClick: PropTypes.func,
    categoryActionLoading: PropTypes.bool,
    onCarActionClick: PropTypes.func.isRequired,
    carSearchValue: PropTypes.string.isRequired,
    onCarSearchChange: PropTypes.func.isRequired,
};

export default CategoryDetailsContent;
